import { Dispatch, SetStateAction, useMemo } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';
import {
  DataTable,
  TableSkeleton,
  Pagination,
  PaginationState,
} from '@/components';
import { PatientProvider } from './types';
const NO_DATA = 'No data available';
interface ProvidersTableProps {
  providers: PatientProvider[];
  totalCount: number;
  isLoading: boolean;
  pagination: PaginationState;
  onPaginationChange: Dispatch<SetStateAction<PaginationState>>;
}

export const ProvidersTable = ({
  providers,
  totalCount,
  isLoading,
  pagination,
  onPaginationChange,
}: ProvidersTableProps) => {
  const columns = useMemo<ColumnDef<PatientProvider>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        meta: { className: 'py-4' },
        cell: ({ row }) => row.original.name || NO_DATA,
      },
      {
        accessorKey: 'specialty',
        header: 'Specialty',
        meta: { className: 'py-4' },
        cell: ({ row }) => row.original.specialty || NO_DATA,
      },
      {
        id: 'phone',
        header: 'Phone',
        meta: { className: 'py-4' },
        cell: ({ row }) => row.original.telephones[0] || NO_DATA,
      },
      {
        id: 'location',
        header: 'Location',
        meta: { className: 'py-4' },
        cell: ({ row }) => {
          const { city, state } = row.original;
          if (city && state) return `${city}, ${state}`;
          if (city) return city;
          if (state) return state;
          return NO_DATA;
        },
      },
      {
        id: 'sourceType',
        header: 'Source Type',
        meta: { className: 'py-4' },
        cell: ({ row }) => row.original.sources[0]?.referenceType || NO_DATA,
      },
      {
        accessorKey: 'createdAt',
        header: 'Date',
        meta: { className: 'py-4' },
        cell: ({ row }) =>
          row.original.createdAt
            ? format(new Date(row.original.createdAt), 'MM/dd/yyyy')
            : NO_DATA,
      },
    ],
    [],
  );

  if (isLoading) {
    return <TableSkeleton rows={5} columns={6} />;
  }

  if (providers.length === 0) {
    return (
      <div className="flex min-h-[300px] flex-col items-center justify-center px-4 py-12">
        <img
          src="/suggested-providers.png"
          alt="No providers to show"
          className="mb-6 max-w-[235px] opacity-80"
        />
        <h3 className="text-xl font-medium">No providers to show</h3>
        <p className="text-muted-foreground mt-2 text-base">
          Providers found in patient medical records and sources will appear
          here.
        </p>
      </div>
    );
  }

  return (
    <div>
      <DataTable columns={columns} data={providers} />
      <Pagination
        pagination={pagination}
        setPagination={onPaginationChange}
        totalCount={totalCount}
        pageSizeOptions={[10, 20, 50]}
      />
    </div>
  );
};
